import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCounter, useLatest } from 'react-use'

import { useRedirectToLogin } from 'hooks/useRedirectToLogin'

import ConnectionMonitor from './ConnectionMonitor'

interface SocketContextType {
  socket: WebSocket
  getNextReqId: (monId: string) => number
  getMonReqId: (monId: string) => number
  getReqMonId: (reqId: number) => string
  isConnected: boolean
  reconnect: () => void
}

const defaultSocketContextValue = {
  socket: null,
  getNextReqId: () => -1,
  getMonReqId: () => -1,
  getReqMonId: () => '',
  isConnected: false,
  reconnect: () => {},
}

export const SocketContext = createContext<SocketContextType>(
  defaultSocketContextValue,
)

type SocketContextProviderProps = {
  children?: JSX.Element | JSX.Element[]
  loginUrl: string
  url: string
}

export const SocketContextProvider: React.FC<SocketContextProviderProps> = ({
  children,
  loginUrl,
  url,
}) => {
  const reqIdRef = useRef(0)
  const monIdsToReqIdsRef = useRef(new Map<string, number>())
  const reqIdsToMonIdsRef = useRef(new Map<number, string>())

  const [socket, setSocket] = useState<WebSocket>()
  const [isConnected, setIsConnected] = useState<boolean>(false)
  const latestIsConnected = useLatest(isConnected)

  const [reconstructionCount, { inc: incrReconstructionCount }] = useCounter()

  const getNextReqId = useCallback(
    (monId: string) => {
      const nextReqId = ++reqIdRef.current
      monIdsToReqIdsRef.current.set(monId, nextReqId)
      reqIdsToMonIdsRef.current.set(nextReqId, monId)
      return nextReqId
    },
    [monIdsToReqIdsRef, reqIdsToMonIdsRef, reqIdRef],
  )

  const getMonReqId = useCallback(
    (monId: string) => monIdsToReqIdsRef.current.get(monId),
    [monIdsToReqIdsRef],
  )

  const getReqMonId = useCallback(
    (reqId: number) => reqIdsToMonIdsRef.current.get(reqId),
    [reqIdsToMonIdsRef],
  )

  const [onOpen, onClose] = useMemo(
    () => [
      () => {
        setIsConnected(true)
      },
      () => {
        setIsConnected(false)
      },
    ],
    [setIsConnected],
  )

  const redirectToLogin = useRedirectToLogin(loginUrl)

  const onError = useCallback(
    (err) => {
      // eslint-disable-next-line no-console
      console.error(err)
      redirectToLogin()
    },
    [redirectToLogin],
  )

  useEffect(() => {
    // TBD: the 2 try/catch here can be removed, the 2 log statements never emit

    let socket: WebSocket

    try {
      socket = new WebSocket(url)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('connection error (1)', err)
    }

    if (socket) {
      socket.addEventListener('open', onOpen)
      socket.addEventListener('close', onClose)
      socket.addEventListener('error', onError)

      setSocket(socket)

      return () => {
        try {
          socket.removeEventListener('open', onOpen)
          socket.removeEventListener('close', onClose)
          socket.removeEventListener('error', onError)

          socket.close()
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('socket error on cleanup (2)', err)
        }
      }
    }
  }, [url, onOpen, onClose, onError, reconstructionCount])

  const reconnect = useCallback(() => {
    if (!latestIsConnected.current) {
      incrReconstructionCount()
    }
  }, [incrReconstructionCount, latestIsConnected])

  const socketContext = useMemo<SocketContextType>(
    () => ({
      socket,
      getNextReqId,
      getMonReqId,
      getReqMonId,
      isConnected,
      reconnect,
    }),
    [socket, getNextReqId, getMonReqId, getReqMonId, isConnected, reconnect],
  )

  return (
    <SocketContext.Provider value={socketContext}>
      {children}
      <ConnectionMonitor />
    </SocketContext.Provider>
  )
}
